import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { postModuleAttempt, ttsAudioUrl } from "@/lib/apiClient";
import { resolveAudioSrc } from "@/lib/offlineCache";
import { checkAchievements } from "@/lib/achievements";
import { pickMime } from "@/lib/audioRecording";
import useElapsedTimer from "@/hooks/useElapsedTimer";
import ModuleFeedbackPrompt from "@/components/ModuleFeedbackPrompt";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import {
  Play, Pause, RotateCcw, CheckCircle2, XCircle, ArrowRight, Mic, Square, Upload, StopCircle,
} from "lucide-react";

// Fisher-Yates — a fresh shuffle every question (and every retry), so the
// correct answer is never in a fixed position across attempts.
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Sound vocab quiz — module 8.
 * Listen-to-audio + 4 Arabic options. Pass at `passingPct`% (configurable).
 *
 * Audio is a clean, isolated AI (TTS) pronunciation of the answer word —
 * NOT a clip cut from a full Qur'an recitation — since a recitation snippet
 * can carry surrounding-word context or tajweed elongation that gives the
 * answer away or muddies the isolated-word listening drill.
 */
export default function SoundMcqMode({ module: m, passingPct, onAttemptSaved }) {
  const { t } = useI18n();
  const { user } = useAuth();
  // Per-word record/upload is a content-authoring tool (reference audio),
  // not a student self-practice feature — staff only.
  const isStaff = ["admin", "support", "mentor"].includes(user?.role);
  const [mode, setMode] = useState("idle"); // idle | running — quiz + timer only start once the user presses Start
  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const audioRef = useRef(null);
  const items = m.items;
  const timer = useElapsedTimer({ autoStart: false });

  // Per-word self-practice recordings (record or upload-from-PC), keyed by
  // the option's Arabic text — practice-only, never submitted/graded.
  const [wordRecordings, setWordRecordings] = useState({});
  const [recordingWord, setRecordingWord] = useState(null);
  const wordRecorder = useRef(null);
  const wordChunks = useRef([]);
  const wordStream = useRef(null);

  useEffect(() => {
    setChosen(null); setPlaying(false); audioRef.current?.pause();
    setShuffledOptions(shuffle(items[idx]?.options || []));
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    Object.values(wordRecordings).forEach((r) => r?.url && URL.revokeObjectURL(r.url));
    wordStream.current?.getTracks().forEach((tr) => tr.stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = items[idx];

  // Resolves and plays the current question's audio unconditionally (no
  // pause-toggle check) — used both by the auto-play effect below and as
  // the first branch of the manual Play/Pause button.
  const playCurrentAudio = async () => {
    if (!audioRef.current) return;
    // Prefer admin-recorded/uploaded audio, then the legacy manual Audio URL
    // field, and only fall back to AI TTS if neither is authored.
    const onlineSrc = current.narrated_audio_url
      ? `${process.env.REACT_APP_BACKEND_URL}${current.narrated_audio_url}`
      : current.audio_url
        ? current.audio_url
        : ttsAudioUrl(current.answer_ar);
    const src = await resolveAudioSrc(onlineSrc);
    if (!audioRef.current) return;
    audioRef.current.src = src;
    audioRef.current.play().then(() => setPlaying(true)).catch(() => toast.error(t("mcq.toast_play_failed")));
  };

  const playAudio = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    playCurrentAudio();
  };

  // Auto-play once per question while the quiz is running — fires both on
  // the very first question (idx stays 0 across the idle→running
  // transition) and on every subsequent question change. Manual Play/Pause
  // still works for replay.
  useEffect(() => {
    if (mode !== "running") return;
    playCurrentAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, mode]);

  const start = () => {
    setMode("running");
    timer.start();
  };

  const startWordRecording = async (ar) => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error(t("mcq.toast_recording_unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      wordStream.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { mimeType: mime });
      wordChunks.current = [];
      rec.ondataavailable = (e) => e.data.size && wordChunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        if (!wordChunks.current.length) return;
        const blob = new Blob(wordChunks.current, { type: rec.mimeType || mime });
        setWordRecordings((prev) => {
          const old = prev[ar];
          if (old?.url) URL.revokeObjectURL(old.url);
          return { ...prev, [ar]: { blob, url: URL.createObjectURL(blob) } };
        });
      };
      wordRecorder.current = rec;
      rec.start();
      setRecordingWord(ar);
    } catch {
      toast.error(t("mcq.toast_mic_failed"));
    }
  };

  const stopWordRecording = () => {
    if (wordRecorder.current && wordRecorder.current.state !== "inactive") {
      try { wordRecorder.current.stop(); } catch { /* harmless */ }
    }
    setRecordingWord(null);
  };

  const uploadWordFile = (ar, file) => {
    if (!file) return;
    setWordRecordings((prev) => {
      const old = prev[ar];
      if (old?.url) URL.revokeObjectURL(old.url);
      return { ...prev, [ar]: { blob: file, url: URL.createObjectURL(file) } };
    });
  };

  const submit = async (correctCount) => {
    timer.stop();
    try {
      const res = await postModuleAttempt(m.code, { module_code: m.code, correct: correctCount, total: items.length });
      setResult(res);
      onAttemptSaved && onAttemptSaved();
      if (res.attempt.passed) checkAchievements();
      toast[res.attempt.passed ? "success" : "error"](
        res.attempt.passed
          ? t("mcq.toast_pass", { score: res.attempt.score_pct })
          : t("mcq.toast_fail", { passing: passingPct, score: res.attempt.score_pct })
      );
    } catch { toast.error(t("mcq.toast_save_failed")); }
  };

  // Ends the quiz early — whatever's answered so far is submitted;
  // unanswered questions count against the score (same `total: items.length`
  // denominator `submit()` already uses).
  const quit = () => { setDone(true); submit(correct); };

  if (done && result) {
    return (
      <div className={`rounded-3xl border-2 p-8 ${result.attempt.passed ? "bg-[#ECFDF5] border-[#10B981]" : "bg-[#FEF2F2] border-[#EF4444]"}`} data-testid="mcq-result">
        <div className="flex items-center gap-3">
          {result.attempt.passed ? <CheckCircle2 className="w-10 h-10 text-[#10B981]" /> : <XCircle className="w-10 h-10 text-[#EF4444]" />}
          <div>
            <div className="font-heading text-2xl font-semibold">{result.attempt.passed ? t("mcq.pass") : t("mcq.fail")} · {result.attempt.score_pct}%</div>
            <div className="text-sm text-[#4B5563]">{t("mcq.passing_score", { passing: passingPct })}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="mcq-result-time">
          <span className="px-2.5 py-1 rounded-full bg-white border border-[#E7E0D2] text-[#1F2937]" data-testid="mcq-result-your-time">
            {t("mcq.time_taken", { s: (timer.elapsed / 1000).toFixed(1) })}
          </span>
          {m.target_seconds > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-white border border-[#E7E0D2] text-[#1F2937]" data-testid="mcq-result-max-time">
              {t("mcq.maximum_time", { s: m.target_seconds })}
            </span>
          )}
          {m.expert_seconds > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-white border border-[#D97706]/40 text-[#B45309]" data-testid="mcq-result-expert-time">
              {t("mcq.expert_time", { s: m.expert_seconds })}
            </span>
          )}
        </div>
        <button onClick={() => { setIdx(0); setCorrect(0); setDone(false); setResult(null); setMode("running"); timer.start(); }}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#064E3B] text-white font-medium hover:bg-[#047857] transition"
                data-testid="mcq-retry">
          <RotateCcw className="w-4 h-4" /> {t("mcq.try_again")}
        </button>

        <ModuleFeedbackPrompt key={result.attempt.created_at} moduleCode={m.code} />
      </div>
    );
  }

  if (mode === "idle") {
    return (
      <div className="rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-7 relative overflow-hidden text-center" data-testid="mcq-mode">
        <div className="absolute inset-0 pattern-islamic opacity-25" />
        <div className="relative py-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FED7AA]">{t("mcq.questions_pass_at", { n: items.length, passing: passingPct })}</div>
          <div className="font-heading text-2xl font-semibold mt-3">{t("mcq.ready_title")}</div>
          <p className="text-sm text-[#FDFBF7]/80 mt-2 max-w-sm mx-auto">{t("mcq.ready_body")}</p>
          <button data-testid="mcq-start" onClick={start}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition">
            <Play className="w-4 h-4" /> {t("mcq.start_test")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-7 relative overflow-hidden" data-testid="mcq-mode">
      <div className="absolute inset-0 pattern-islamic opacity-25" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FED7AA]">{t("mcq.listen_progress", { n: idx + 1, total: items.length })}</div>
          <div className="flex items-center gap-3">
            <div data-testid="mcq-timer" className="font-heading text-lg font-light tabular-nums text-white">
              {(timer.elapsed / 1000).toFixed(1)}<span className="text-xs text-[#FDFBF7]/70">s</span>
            </div>
            <div className="text-xs text-[#FDFBF7]/70">{t("mcq.pass_at", { passing: passingPct })}</div>
            <button data-testid="mcq-stop" onClick={quit} title={t("mcq.end_quiz_now")}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-[#FED7AA] text-xs font-medium transition">
              <StopCircle className="w-3.5 h-3.5" /> {t("mcq.stop")}
            </button>
          </div>
        </div>

        <button
          data-testid="mcq-play"
          onClick={playAudio}
          className={`mt-5 mx-auto block w-20 h-20 rounded-full bg-[#D97706] hover:bg-[#B45309] grid place-items-center transition ${playing ? "recording-ring" : ""}`}>
          {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white pl-1" />}
        </button>
        <audio ref={audioRef} onEnded={() => setPlaying(false)} preload="none" />

        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
          {shuffledOptions.map((o) => {
            const isAnswer = o.ar === current.answer_ar;
            const picked = chosen === o.ar;
            let cls = "bg-white text-[#1F2937] border-white hover:bg-[#FFF7ED]";
            if (chosen) {
              if (isAnswer) cls = "bg-[#ECFDF5] text-[#064E3B] border-[#10B981]";
              else if (picked) cls = "bg-[#FEF2F2] text-[#7F1D1D] border-[#EF4444]";
              else cls = "bg-white/40 text-white/70 border-white/40";
            }
            const rec = wordRecordings[o.ar];
            const isRecording = recordingWord === o.ar;
            return (
              <div key={o.ar} className={`rounded-2xl border-2 transition overflow-hidden ${cls}`}>
                <button data-testid={`mcq-option-${o.ar}`} disabled={!!chosen}
                  onClick={() => {
                    setChosen(o.ar);
                    if (isAnswer) setCorrect((c) => c + 1);
                  }}
                  className="w-full p-4 text-right disabled:cursor-default">
                  <div className="font-quran text-2xl leading-loose">{o.ar}</div>
                  <div className="text-[11px] italic mt-1">{o.translit}</div>
                </button>
                {/* Staff-only: record/upload reference pronunciation audio per word. */}
                {isStaff && (
                  <div className="flex items-center gap-2 px-3 pb-2.5 pt-1.5 border-t border-current/10">
                    <button type="button" data-testid={`mcq-word-record-${o.ar}`}
                      onClick={() => (isRecording ? stopWordRecording() : startWordRecording(o.ar))}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/5 hover:bg-black/10 transition"
                      title={isRecording ? t("mcq.stop_recording") : t("mcq.record_reference")}>
                      {isRecording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                    </button>
                    <label data-testid={`mcq-word-upload-${o.ar}`}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/5 hover:bg-black/10 transition cursor-pointer"
                      title={t("mcq.upload_from_pc")}>
                      <Upload className="w-3.5 h-3.5" />
                      <input type="file" accept="audio/*" hidden onChange={(e) => uploadWordFile(o.ar, e.target.files?.[0])} />
                    </label>
                    {rec?.url && (
                      <audio data-testid={`mcq-word-audio-${o.ar}`} controls preload="metadata" src={rec.url} className="h-7 flex-1 min-w-0" />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            data-testid="mcq-next"
            disabled={!chosen}
            onClick={() => {
              if (idx + 1 >= items.length) {
                setDone(true);
                submit(correct + (chosen === current.answer_ar ? 0 : 0)); // chosen already counted on click
              } else setIdx(idx + 1);
            }}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] disabled:opacity-30 text-white font-medium transition">
            {idx + 1 === items.length ? t("mcq.finish") : t("mcq.next")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      </div>
    </>
  );
}
